import type { SWRResponse } from 'swr';

import { useEffect } from 'react';

/**
 * If the user is unauthorized, redirect them to the login page
 */
function useUnauthorizedRedirect(error: SWRResponse['error'] | undefined) {
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (error?.status === 401) {
      const errorHeaders = (error as { headers?: Record<string, string | undefined> })?.headers;
      // The server sends 'X-Expiry-Reason', but the Fetch API Headers.entries() normalizes it to 'x-expiry-reason'
      const expiryReason = errorHeaders?.['x-expiry-reason'];

      // Temporarily clear beforeunload handler to prevent confirmation dialogs when expiry reason exists
      if (expiryReason) {
        window.onbeforeunload = null;
      }

      // Deferring this call with a timer to avoid any race conditions with components
      // trying to remove a `beforeunload` listener before the redirect occurs
      timer = setTimeout(() => {
        // Build query string manually to match expected test format (unencoded redirect)
        const params = [`redirect=${encodeURIComponent(window.location.pathname)}`];
        if (expiryReason) {
          params.push(`timeoutReason=${expiryReason}`);
        }

        const loginUrl = `/login?${params.join('&')}`;
        window.location.assign(loginUrl);
      }, 100);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [error]);
}

export default useUnauthorizedRedirect;
